import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, Outlet, Navigate } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import config from '../config';

// Create a context for public display data with a default value
export const PublicDisplayContext = createContext({ publicToken: null });

// Simple display token validation - this is not for security, just to prevent casual access
const VALID_DISPLAY_TOKENS = ['display123', 'signage456', 'screen789']; // You can change these to any values you want

function PublicDisplayWrapper() {
  const { displayToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  
  useEffect(() => {
    // Validate the display token
    if (!displayToken || !VALID_DISPLAY_TOKENS.includes(displayToken)) {
      setError('Invalid display token');
      setLoading(false);
      return;
    }
    
    // Generate a temporary public API token
    const fetchPublicToken = async () => {
      try {
        // For now, we'll just use the display token as the public token
        // In a real implementation, you might want to exchange this for a temporary token from the server
        setPublicToken(displayToken);
        setLoading(false);
      } catch (error) {
        console.error('Error setting up public display:', error);
        setError('Failed to initialize display');
        setLoading(false);
      }
    };
    
    fetchPublicToken();
  }, [displayToken]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
        <Typography variant="h4" color="error" align="center">
          {error}
        </Typography>
      </Box>
    );
  }
  
  // Provide the public token to child components
  return (
    <PublicDisplayContext.Provider value={{ publicToken }}>
      <Box sx={{ 
        height: '100vh', 
        overflow: 'hidden',
        bgcolor: '#f5f5f5'
      }}>
        <Outlet />
      </Box>
    </PublicDisplayContext.Provider>
  );
}

// Custom hook to use the public display context
export const usePublicDisplay = () => {
  // Try to get the context, but don't throw if it's not available
  try {
    return useContext(PublicDisplayContext);
  } catch (error) {
    // Return the default context value if there's an error
    console.warn('usePublicDisplay used outside of PublicDisplayProvider, using default value');
    return { publicToken: null };
  }
};

export default PublicDisplayWrapper; 